import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  Bell,
  Globe,
  Palette,
  Info,
  Link2,
  Check,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Button, Switch } from "@tabipla/ui";
import { useLinkLine } from "@tabipla/hooks/auth";
import { MekkeLayout } from "../layouts/MekkeLayout";
import { useAuth } from "@/application/hooks/useAuth";
import { useSettings, useUpdateSettings } from "@/application/hooks/useSettings";
import { linkSocial } from "@/infrastructure/auth/auth-client";

export function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();
  const { isLinkingLine, lineError, handleLinkLine } = useLinkLine({
    linkSocial,
    searchParams,
    setSearchParams,
    onSuccess: () => toast.success(t("settings.lineLinkSuccess", "LINEアカウントを連携しました")),
    t,
  });

  const settingsSections = [
    {
      title: t("settings.account"),
      icon: User,
      items: [{ label: t("profile.editProfile"), action: () => console.log("Edit profile") }],
    },
    {
      title: t("settings.notifications"),
      icon: Bell,
      items: [{ label: t("settings.notifications"), action: () => console.log("Notifications") }],
    },
    {
      title: t("settings.language"),
      icon: Globe,
      items: [{ label: "日本語", action: () => console.log("Language") }],
    },
    {
      title: t("settings.theme"),
      icon: Palette,
      items: [{ label: "システム設定", action: () => console.log("Theme") }],
    },
    {
      title: t("settings.about"),
      icon: Info,
      items: [{ label: `${t("settings.version")} 0.1.0`, action: () => {} }],
    },
  ];

  return (
    <MekkeLayout hideNav>
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-14 items-center gap-4 px-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">{t("settings.title")}</h1>
          </div>
        </header>

        {/* Settings List */}
        <div className="px-4 py-4">
          {/* LINE Integration */}
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <svg className="h-4 w-4 text-[#06C755]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
              </svg>
              {t("settings.lineIntegration", "LINE連携")}
            </div>
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-3">
              <div>
                <p className="font-medium">
                  {user?.lineUserId
                    ? t("settings.lineConnected", "LINE連携済み")
                    : t("settings.lineNotConnected", "LINE未連携")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {user?.lineUserId
                    ? t("settings.lineConnectedDescription", "LINEアカウントが連携されています")
                    : t(
                        "settings.lineNotConnectedDescription",
                        "LINEでログインできるようになります"
                      )}
                </p>
              </div>
              {user?.lineUserId ? (
                <div className="flex items-center gap-2 text-sm text-[#06C755]">
                  <Check className="h-4 w-4" />
                  {t("settings.connected", "連携中")}
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLinkLine}
                  disabled={isLinkingLine}
                  className="border-[#06C755] text-[#06C755] hover:bg-[#06C755]/10"
                >
                  {isLinkingLine ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Link2 className="mr-2 h-4 w-4" />
                  )}
                  {t("settings.linkLine", "連携")}
                </Button>
              )}
            </div>
            {lineError && (
              <div className="mt-2 flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {lineError}
              </div>
            )}
            {user?.lineUserId && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-[#06C755]/30 bg-[#06C755]/5 px-3 py-3">
                <div>
                  <p className="font-medium">{t("settings.lineNotifications", "LINE通知")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.lineNotificationsDescription", "LINEで通知を受け取る")}
                  </p>
                </div>
                <Switch
                  checked={settings?.lineNotifications ?? false}
                  onCheckedChange={() =>
                    updateSettings.mutate({ lineNotifications: !settings?.lineNotifications })
                  }
                  disabled={updateSettings.isPending}
                />
              </div>
            )}
          </div>

          {settingsSections.map((section) => {
            const Icon = section.icon;
            return (
              <div key={section.title} className="mb-6">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Icon className="h-4 w-4" />
                  {section.title}
                </div>
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <button
                      key={item.label}
                      onClick={item.action}
                      className="w-full rounded-lg px-3 py-3 text-left transition-colors hover:bg-muted"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MekkeLayout>
  );
}
